/**
 * RecurringSchedulesSection — Settings → Recurring invoices.
 *
 * Lists the tenant's recurring schedules and exposes the lifecycle actions:
 * Generate now (`runNow`), Pause / Resume + Cancel (`setStatus`), and New
 * schedule (the create dialog). The daily `recurring-schedules-tick` cron is
 * what fires them automatically; this is the human management surface.
 *
 * @see prototype/automations/recurring-schedules.html
 * @see ADR-0024
 */
import { useState } from "react";
import { Alert, Button, ConfirmDialog, cn, toast } from "@flint/ui";
import { DataTableCard, ListPagination } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { formatDate } from "../../../lib/format.ts";
import { NewScheduleDialog } from "./new-schedule-dialog.tsx";
import { cadenceLabel, formatSourceAmount } from "./format.ts";

type ScheduleStatus = "active" | "paused" | "cancelled" | "completed";

const STATUS_CLASS: Record<ScheduleStatus, string> = {
  active: "bg-sky-50 text-sky-700 border-sky-100",
  paused: "bg-warn-50 text-warn border-warn-100",
  cancelled: "bg-bad-50 text-bad border-bad-100",
  completed: "bg-raised text-muted border-line",
};
const STATUS_LABEL: Record<ScheduleStatus, string> = {
  active: "Active",
  paused: "Paused",
  cancelled: "Cancelled",
  completed: "Completed",
};

export function RecurringSchedulesSection() {
  const utils = trpc.useUtils();
  const list = trpc.recurringSchedules.list.useQuery();
  const invalidate = () => utils.recurringSchedules.list.invalidate();

  const runNow = trpc.recurringSchedules.runNow.useMutation({
    onSuccess: (r) => {
      void invalidate();
      void utils.invoices.list.invalidate();
      void utils.bills.list.invalidate();
      toast.success("Draft created", {
        description:
          r.targetType === "bill"
            ? "A new draft bill is ready to review under Bills."
            : "A new draft invoice is ready to review under Invoices.",
      });
    },
    onError: (e) => toast.error("Couldn't generate", { description: e.message }),
  });
  const setStatus = trpc.recurringSchedules.setStatus.useMutation({
    onSuccess: () => void invalidate(),
    onError: (e) => toast.error("Couldn't update schedule", { description: e.message }),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);

  const items = list.data ?? [];
  const activeCount = items.filter((s) => (s.status as ScheduleStatus) === "active").length;
  const pausedCount = items.filter((s) => (s.status as ScheduleStatus) === "paused").length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted max-w-[60ch]">
          Each run clones its source document into a fresh{" "}
          <strong className="text-ink-2">draft</strong> for review — Flint never issues, posts, or
          sends on its own in V1. Schedules fire daily at 6:30&nbsp;AM IST.
        </p>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          New schedule
        </Button>
      </div>

      {list.error ? (
        <Alert intent="bad" title="Couldn't load schedules" description={list.error.message} />
      ) : null}

      {list.isLoading ? (
        <div className="rounded-md border border-line bg-paper shadow-sm overflow-hidden">
          <p className="p-6 text-sm text-muted">Loading schedules…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-line bg-paper shadow-sm overflow-hidden">
          <div className="p-8 text-center">
            <p className="text-sm font-semibold text-ink">No recurring schedules yet</p>
            <p className="mt-1 text-sm text-muted">
              Set one up to auto-draft an invoice every month, quarter, or year.
            </p>
            <Button size="sm" className="mt-4" onClick={() => setDialogOpen(true)}>
              New schedule
            </Button>
          </div>
        </div>
      ) : (
        <DataTableCard
          footer={[
            { label: "Schedules", value: items.length },
            { label: "Active", value: activeCount },
            { label: "Paused", value: pausedCount },
          ]}
          pagination={
            <ListPagination
              pageNumber={1}
              showingFrom={items.length === 0 ? 0 : 1}
              showingTo={items.length}
              total={items.length}
              sortLabel="by next run"
              hasPrev={false}
              hasNext={false}
              onPrev={() => {}}
              onNext={() => {}}
            />
          }
        >
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-raised text-xs uppercase tracking-[0.04em] text-muted">
                <th className="text-left font-semibold px-4 py-2.5">Schedule</th>
                <th className="text-left font-semibold px-4 py-2.5">Source</th>
                <th className="text-left font-semibold px-4 py-2.5">Cadence</th>
                <th className="text-left font-semibold px-4 py-2.5 whitespace-nowrap">Next run</th>
                <th className="text-left font-semibold px-4 py-2.5">Runs</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="text-left font-semibold px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const status = row.status as ScheduleStatus;
                const terminal = status === "cancelled" || status === "completed";
                const busy =
                  (runNow.isPending && runNow.variables?.scheduleId === row.id) ||
                  (setStatus.isPending && setStatus.variables?.scheduleId === row.id);
                return (
                  <tr key={row.id} className="border-t border-line align-top hover:bg-raised/60">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-ink">{row.label}</span>
                        <span
                          className={cn(
                            "inline-flex items-center rounded-sm border px-1.5 py-px text-xs font-semibold uppercase tracking-wider",
                            row.targetType === "bill"
                              ? "bg-warn-50 text-warn border-warn-100"
                              : "bg-sky-50 text-sky-700 border-sky-100",
                          )}
                        >
                          {row.targetType === "bill" ? "Bill" : "Invoice"}
                        </span>
                      </div>
                      <div className="text-xs text-muted mt-0.5">
                        {row.mode === "draft" ? "Drafts for review" : "Auto-issue"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {row.source ? (
                        <>
                          <div className="font-mono text-xs text-ink-2">
                            {row.source.docNumber ?? "—"}
                          </div>
                          <div className="text-xs text-muted mt-0.5">
                            {row.source.counterpartyName}
                          </div>
                          <div className="font-mono text-xs font-semibold text-ink mt-1">
                            {formatSourceAmount(row.source)}
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-muted">Source removed</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>{cadenceLabel(row).split(" · ")[0]}</div>
                      <div className="text-xs text-muted mt-0.5">{cadenceLabel(row).split(" · ")[1]}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                      {formatDate(row.nextRunOn)}
                    </td>
                    <td className="px-4 py-3">{row.runsCount}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold",
                          STATUS_CLASS[status],
                        )}
                      >
                        {STATUS_LABEL[status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {!terminal ? (
                          <>
                            <Button
                              size="sm"
                              intent="link"
                              disabled={busy}
                              onClick={() => runNow.mutate({ scheduleId: row.id })}
                            >
                              Generate now
                            </Button>
                            <Button
                              size="sm"
                              intent="ghost"
                              disabled={busy}
                              onClick={() =>
                                setStatus.mutate({
                                  scheduleId: row.id,
                                  action: status === "paused" ? "resume" : "pause",
                                })
                              }
                            >
                              {status === "paused" ? "Resume" : "Pause"}
                            </Button>
                            <Button
                              size="sm"
                              intent="ghost"
                              className="text-bad hover:bg-bad-50"
                              disabled={busy}
                              onClick={() => setCancelId(row.id)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs text-muted">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DataTableCard>
      )}

      <div className="flex flex-wrap gap-4 text-xs text-muted px-0.5">
        <Legend cls={STATUS_CLASS.active} label="Active" note="firing on cadence" />
        <Legend cls={STATUS_CLASS.paused} label="Paused" note="skips runs until resumed" />
        <Legend cls={STATUS_CLASS.cancelled} label="Cancelled" note="stopped for good" />
        <Legend cls={STATUS_CLASS.completed} label="Completed" note="reached its end date" />
      </div>

      <NewScheduleDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={() => void invalidate()} />

      <ConfirmDialog
        open={cancelId !== null}
        onOpenChange={(o) => !o && setCancelId(null)}
        title="Cancel this schedule?"
        description="It stops generating immediately and can't be reactivated. Documents already generated are untouched."
        confirmLabel="Cancel schedule"
        cancelLabel="Keep it"
        intent="danger"
        pending={setStatus.isPending}
        onConfirm={async () => {
          if (cancelId) {
            await setStatus.mutateAsync({ scheduleId: cancelId, action: "cancel" });
            setCancelId(null);
          }
        }}
      />
    </div>
  );
}

function Legend({ cls, label, note }: { cls: string; label: string; note: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 font-semibold", cls)}>
        {label}
      </span>
      {note}
    </span>
  );
}
